import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
export interface WorldClock {
  city: string;
  time: string; // HH:MM
  utcOffset: number;
  isReference?: boolean;
}
export interface WorldClockPanelProps {
  clocks: WorldClock[];
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function WorldClockPanel({
  clocks,
  label,
  mode = 'support'
}: WorldClockPanelProps) {
  const isStimulus = mode === 'stimulus';
  // Sort clocks so reference is first, or keep original order if no reference
  const sortedClocks = [...clocks].sort((a, b) => {
    if (a.isReference && !b.isReference) return -1;
    if (!a.isReference && b.isReference) return 1;
    return 0;
  });
  const formatOffset = (offset: number) => {
    return offset >= 0 ? `UTC +${offset}` : `UTC ${offset}`;
  };
  const calculateDifference = (clock1: WorldClock, clock2: WorldClock) => {
    const diff = clock2.utcOffset - clock1.utcOffset;
    return diff > 0 ? `+${diff} uur` : `${diff} uur`;
  };
  return <div className={`${isStimulus ? 'bg-sky-50 rounded-xl px-4 pt-4 pb-6 border border-sky-200' : 'bg-gradient-to-br from-sky-50 to-blue-50 p-6 rounded-2xl border-2 border-sky-200 shadow-sm'}`} aria-label="Wereldklokken paneel" data-id="element-10327">
      {label && <p className={`text-sm font-semibold text-sky-800 mb-6 text-center ${!isStimulus ? 'text-base' : ''}`} data-id="element-10328">
          {label}
        </p>}

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-10329">
        {sortedClocks.map((clock, index) => <Fragment key={clock.city} data-id="element-10330">
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className={`flex flex-col items-center p-4 rounded-xl min-w-[120px] ${clock.isReference ? 'bg-white border-2 border-sky-400 shadow-md' : 'bg-white/80 border border-sky-200 shadow-sm'}`} data-id="element-10331">
              <span className="text-sm font-medium text-slate-500 mb-1" data-id="element-10332">
                {clock.city}
              </span>
              <span className="text-2xl font-bold text-slate-800 font-mono tracking-wider mb-2" data-id="element-10333">
                {clock.time}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${clock.isReference ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-10334">
                {formatOffset(clock.utcOffset)}
              </span>
            </motion.div>
            {/* Arrow between clocks */}
            {index < sortedClocks.length - 1 && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: index * 0.1 + 0.2
        }} className="flex flex-col items-center text-sky-400" data-id="element-10335">
                {!isStimulus && <span className="text-xs font-bold text-sky-600 mb-1 whitespace-nowrap" data-id="element-10336">
                    {calculateDifference(clock, sortedClocks[index + 1])}
                  </span>}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hidden sm:block" data-id="element-10337">
                  <line x1="5" y1="12" x2="19" y2="12" data-id="element-10338"></line>
                  <polyline points="12 5 19 12 12 19" data-id="element-10339"></polyline>
                </svg>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block sm:hidden" data-id="element-10340">
                  <line x1="12" y1="5" x2="12" y2="19" data-id="element-10341"></line>
                  <polyline points="19 12 12 19 5 12" data-id="element-10342"></polyline>
                </svg>
              </motion.div>}
          </Fragment>)}
      </div>

      {!isStimulus && sortedClocks.length >= 2 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.5
    }} className="mt-6 bg-white/80 rounded-xl p-4 border border-sky-100 text-sm text-slate-700" data-id="element-10343">
          <div className="flex items-start gap-2" data-id="element-10344">
            <span className="text-lg" data-id="element-10345">🌍</span>
            <div data-id="element-10346">
              <p className="font-semibold text-sky-800 mb-1" data-id="element-10347">
                Tijdverschil berekenen
              </p>
              <p data-id="element-10348">
                Naar het <strong data-id="element-10349">oosten</strong> (later op de dag) = uren erbij
                optellen (+).
                <br data-id="element-10350" />
                Naar het <strong data-id="element-10351">westen</strong> (vroeger op de dag) = uren eraf
                halen (−).
              </p>
            </div>
          </div>
        </motion.div>}
    </div>;
}